import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cx } from './cx';

export type BadgeTone = 'ok' | 'todo' | 'warn' | 'danger' | 'mute' | 'info';

const tones: Record<BadgeTone, string> = {
  ok: 'bg-success-light text-success-dark',
  todo: 'bg-primary-50 text-primary-600',
  warn: 'bg-warn-bg text-warn-fg',
  danger: 'bg-danger-soft text-danger-dark',
  mute: 'bg-surface-2 text-secondary',
  info: 'bg-blue-100 text-blue-800',
};

interface BadgeProps {
  tone?: BadgeTone;
  size?: 'sm' | 'lg';
  /** Pastille ronde avant le libellé ; `live` la fait pulser (état en cours). */
  dot?: boolean | 'live';
  children: ReactNode;
  className?: string;
}

/** Statut court en majuscules (gris = information, jamais cliquable). */
export function Badge({ tone = 'mute', size = 'sm', dot, children, className }: BadgeProps) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill font-bold uppercase leading-[1.4] tracking-[.5px]',
        size === 'lg' ? 'px-3 py-1.5 text-[11px]' : 'px-2.5 py-1 text-[10px]',
        tones[tone],
        className,
      )}
    >
      {dot && <StatusDot live={dot === 'live'} />}
      {children}
    </span>
  );
}

/** Point de statut, de la couleur du texte courant. */
export function StatusDot({ live = false, className }: { live?: boolean; className?: string }) {
  return (
    <span className={cx('relative inline-block h-2 w-2 shrink-0', className)} aria-hidden>
      <span className="absolute inset-0 rounded-full bg-current" />
      {live && <span className="absolute -inset-[3px] animate-ping-soft rounded-full bg-current opacity-40 motion-reduce:animate-none" />}
    </span>
  );
}

/** Pastille « Bientôt » : fonctionnalité gardée volontairement mais pas encore développée. */
export function SoonBadge({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        'ml-1.5 inline-flex rounded-pill bg-surface-2 px-[7px] py-px align-[1px] text-[10px] font-semibold tracking-[.3px] text-muted',
        className,
      )}
    >
      Bientôt
    </span>
  );
}

interface ChipProps {
  icon?: LucideIcon;
  muted?: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}

/** Pilule d'information (icône + texte). Devient cliquable si `onClick` est fourni. */
export function Chip({ icon: Icon, muted = false, onClick, children, className }: ChipProps) {
  const classes = cx(
    'inline-flex h-8 items-center gap-[7px] rounded-pill border border-line bg-white px-3 text-[13px] transition-all duration-200',
    muted ? 'text-secondary' : 'text-body',
    onClick && 'cursor-pointer hover:border-primary-200 hover:shadow-navbar',
    className,
  );
  const content = (
    <>
      {Icon && <Icon size={15} strokeWidth={1.9} className={muted ? 'text-muted' : 'text-primary'} aria-hidden />}
      {children}
    </>
  );
  return onClick ? (
    <button type="button" className={classes} onClick={onClick}>
      {content}
    </button>
  ) : (
    <span className={classes}>{content}</span>
  );
}
